// Photo rules shared by web and mobile (IMPLEMENTATION_PLAN §3.4 R2). The database enforces the count
// (assets_photos_max_3) and the bucket enforces ≤ 2 MB JPEG/PNG/WebP; the apps downsize before upload so a
// phone photo (4–12 MB) never leaves the device at full size.

/** At most this many photos per asset (the tag mounting photo is separate and does not count). */
export const PHOTO_MAX_PER_ASSET = 3;
/** Longest side after downsizing, in pixels. */
export const PHOTO_MAX_SIDE = 1600;
/** JPEG quality of the re-encoded image (0–1). */
export const PHOTO_JPEG_QUALITY = 0.8;
/** The bucket's limit — a downsized photo is far below it. */
export const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

export interface AssetPhoto {
  path: string;
  /** user id of who added it */
  by: string;
  at: string;
}

/** Size that fits inside `max` × `max` keeping the aspect ratio; never enlarges. */
export function fitWithin(
  width: number,
  height: number,
  max: number = PHOTO_MAX_SIDE,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Storage path for an asset photo: the policies check the account folder, the RPC the asset folder. */
export function assetPhotoPath(companyId: string, assetId: string, fileId: string): string {
  return `${companyId}/assets/${assetId}/${fileId}.jpg`;
}

/** Who may remove a photo: managers / super admins, or the member who added it (the RPC decides; UI mirrors). */
export function canRemovePhoto(
  photo: AssetPhoto,
  me: { user_id: string; role: string | null; platform_admin: boolean } | undefined,
): boolean {
  return !!me && (me.platform_admin || me.role === 'manager' || photo.by === me.user_id);
}
